import type { Theme } from '@mui/material/styles';
import { getZoomAdjustedPx } from './zoom';

const SPACING_UNIT_BY_KEY = {
  xxs: 0.25,
  xs: 0.5,
  s: 1,
  m: 2,
  l: 3,
  xl: 4,
} as const;

type ThemeSpacingKey = keyof typeof SPACING_UNIT_BY_KEY;
type ThemeTypographyVariant = 'overline' | 'caption' | 'body2' | 'body1' | 'subtitle1';
type ThemeTypographyPxProperty = 'fontSize' | 'lineHeight';

const parseThemePx = (value: number | string | undefined, fallback: number) => {
  if (typeof value === 'number') {
    return value;
  }

  if (!value) {
    return fallback;
  }

  const parsedValue = Number.parseFloat(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

export const getThemeSpacingPx = (theme: Theme, spacingKey: ThemeSpacingKey) =>
  parseThemePx(theme.spacing(SPACING_UNIT_BY_KEY[spacingKey]), 0);

export const getZoomAdjustedThemeSpacingPx = (
  theme: Theme,
  spacingKey: ThemeSpacingKey,
  zoomLevel: number
) => getZoomAdjustedPx(getThemeSpacingPx(theme, spacingKey), zoomLevel);

export const getThemeTypographyPx = (
  theme: Theme,
  variant: ThemeTypographyVariant,
  property: ThemeTypographyPxProperty
) => parseThemePx(theme.typography[variant]?.[property], 0);

export const getZoomAdjustedThemeTypographyPx = (
  theme: Theme,
  variant: ThemeTypographyVariant,
  property: ThemeTypographyPxProperty,
  zoomLevel: number
) => getZoomAdjustedPx(getThemeTypographyPx(theme, variant, property), zoomLevel);
